import { useCallback, useEffect, useState } from 'react';
import { showToastError } from '../components/ToastNotifications';
import type { BrandRecord, MarketRecord, PagedResult, TemplateRecord } from '../types/index';
import { getAllBrands } from './useBrands';
import { getAllMarkets } from './useMarkets';
import { getTemplatesTotals } from './useTemplates';

export default function useTemplatesPageResults({ organizationId, filters, sort }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [state, setState] = useState<{
    organizationId: unknown;
    results: PagedResult<TemplateRecord>;
    marketOptions: MarketRecord[];
    brandOptions: BrandRecord[];
  }>({
    organizationId,
    results: { page: filters.page, pageSize: filters.pageSize, total: 0, data: [] },
    marketOptions: [],
    brandOptions: [],
  });
  const refreshResults = useCallback(() => setRefreshKey((key) => key + 1), []);

  useEffect(() => {
    let current = true;
    setState((value) => value.organizationId === organizationId ? value : {
        organizationId,
        results: { page: filters.page, pageSize: filters.pageSize, total: 0, data: [] },
        marketOptions: [],
        brandOptions: [],
      });
    if (!organizationId) return () => { current = false; };

    getAllMarkets(organizationId)
      .then((markets) => {
        if (current) setState((value) => ({
          ...value,
          marketOptions: markets.sort((a, b) => (a.name || a.code).localeCompare(b.name || b.code)),
        }));
      })
      .catch(() => { if (current) showToastError('Market data could not be loaded. Please refresh the page or try again.'); });
    getAllBrands(organizationId)
      .then((brands) => {
        if (current) setState((value) => ({
          ...value,
          brandOptions: brands.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
        }));
      })
      .catch(() => { if (current) showToastError('Brand data could not be loaded. Please refresh the page or try again.'); });

    return () => { current = false; };
  }, [organizationId]);

  useEffect(() => {
    let current = true;
    if (!organizationId) return () => { current = false; };

    getTemplatesTotals({
      organizationId,
      page: filters.page,
      pageSize: filters.pageSize,
      name: filters.name || null,
      marketId: filters.marketId,
      brandId: filters.brandId,
      active: filters.active,
      sortField: sort.fieldName,
      sortAscending: sort.isAscending,
      lastModifiedSortAscending: sort.fieldName === 'lastModified' ? sort.isAscending : null,
      markets: state.organizationId === organizationId ? state.marketOptions : [],
    }).then((results) => {
      if (current) setState((value) => ({ ...value, results }));
    }).catch(() => undefined);

    return () => { current = false; };
  }, [filters, organizationId, refreshKey, sort.fieldName, sort.isAscending, state.marketOptions]);

  if (state.organizationId !== organizationId) {
    return {
      results: { page: 0, pageSize: filters.pageSize, total: 0, data: [] } as PagedResult<TemplateRecord>,
      marketOptions: [],
      brandOptions: [],
      refreshResults,
    };
  }
  return { ...state, refreshResults };
}
